import { DEVICE_TYPE_IDS, PROJECT_UNIT_INSTANCES_DATE_STATUS, maxTimeWithoutResponse } from './constants';
import { ConfigData, UnitTsData } from './utils.types';
import { isBefore, isFuture, isPast, max, min } from 'date-fns';
import { GreenerUnitInstance, Project, Range, Unit } from 'types';

export function extractUnitsByDevice(units: Unit[], deviceTypeId: number): Unit[] {
  if (!Object.values(DEVICE_TYPE_IDS).includes(deviceTypeId)) {
    throw new Error(`Invalid device type id: ${deviceTypeId}`);
  }

  return units.filter(
    (unit) =>
      unit.watcherType.deviceTypeId === deviceTypeId || unit.watcherType.deviceType?.deviceTypeId === deviceTypeId
  );
}

export function extractBatteries(units: Unit[]): Unit[] {
  return extractUnitsByDevice(units, DEVICE_TYPE_IDS.BATTERY);
}

export function extractEnergyMeters(units: Unit[]): Unit[] {
  return extractUnitsByDevice(units, DEVICE_TYPE_IDS.ENERGY_METER);
}

export function filterActiveUnits(unitInstances: GreenerUnitInstance[], latestDate: Date): Unit[] {
  const activeUnits: Unit[] = [];
  const now = new Date();
  const refDate = new Date(Math.min(...[latestDate.getTime(), now.getTime()]));

  unitInstances.forEach((instance) => {
    if (new Date(instance.tsStart) < refDate && new Date(instance.tsEnd) > refDate) {
      activeUnits.push(instance.greenerUnit);
    }
  });
  return activeUnits;
}

export const getExternalIdentifierFromUnitInstance = (instance: GreenerUnitInstance): string | undefined =>
  instance && instance.greenerUnit.externalIdentifier;

export const getUnitTypeFromUnitInstance = (instance: GreenerUnitInstance): number | undefined =>
  instance &&
  (instance.greenerUnit.watcherType.deviceTypeId || instance.greenerUnit.watcherType.deviceType?.deviceTypeId);

export function constructConfigTableObject(configData: ConfigData[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  configData.forEach((configOption) => {
    result[configOption.name] = configOption.val;
  });

  return result;
}
export function getIsOnline(unitTsData: UnitTsData): boolean {
  const before = Date.now() - maxTimeWithoutResponse;
  const tsMeasured = unitTsData?.ts_measured ? unitTsData?.ts_measured * 1000 : 0;

  return isBefore(before, tsMeasured);
}

export function getUnitInstancesDateStatus(
  unitInstances: GreenerUnitInstance[],
  unitExtId: string
): string | undefined {
  let unitWillBecomeActive = false;
  let unitWasActive = false;
  let unitIsActive = false;

  const unitSelectedInstances = unitInstances.filter(
    (instance) => instance.greenerUnit.externalIdentifier === unitExtId
  );
  for (const instance of unitSelectedInstances) {
    if (isPast(new Date(instance.tsStart)) && isFuture(new Date(instance.tsEnd))) {
      unitIsActive = true;
    } else if (!instance.tsEnd && isPast(new Date(instance.tsStart))) {
      unitIsActive = true;
    } else if (isFuture(new Date(instance.tsStart))) {
      unitWillBecomeActive = true;
    } else if (instance.tsEnd && isPast(new Date(instance.tsEnd))) {
      unitWasActive = true;
    }
  }

  if (unitIsActive) {
    return PROJECT_UNIT_INSTANCES_DATE_STATUS.active;
  } else if (unitWasActive && unitWillBecomeActive) {
    return PROJECT_UNIT_INSTANCES_DATE_STATUS.inBetween;
  } else if (unitWillBecomeActive) {
    return PROJECT_UNIT_INSTANCES_DATE_STATUS.future;
  } else if (unitWasActive) {
    return PROJECT_UNIT_INSTANCES_DATE_STATUS.past;
  }
}

export function determineUnitAvailableDateRange(
  project?: Project | null,
  unitInstances?: GreenerUnitInstance[],
  unitExtId?: string
): Range {
  if (!unitInstances?.length) {
    return { from: null, to: null };
  }

  const currentOrPastInstances = unitInstances?.filter(
    (instance) => instance.greenerUnit.externalIdentifier === unitExtId && isPast(new Date(instance.tsStart))
  );

  if (!currentOrPastInstances.length) {
    const firstStartDate = min(unitInstances.map((instance) => new Date(instance.tsStart)));

    const firstInstance = unitInstances.find(
      (instance) => new Date(instance.tsStart).getTime() === firstStartDate.getTime()
    );

    return {
      from: new Date(firstInstance!.tsStart),
      to: firstInstance!.tsEnd ? new Date(firstInstance!.tsEnd) : null,
    };
  }

  const latestStartDate = max(currentOrPastInstances.map((instance) => new Date(instance.tsStart)));

  const latestInstance = currentOrPastInstances.find(
    (instance) => new Date(instance.tsStart).getTime() === latestStartDate.getTime()
  );

  if (!!project && project?.tsEnd && isPast(new Date(project?.tsEnd))) {
    return {
      from: new Date(latestInstance!.tsStart || project.tsStart!),
      to: new Date(latestInstance!.tsEnd || project.tsEnd!),
    };
  }

  return {
    from: new Date(latestInstance!.tsStart),
    to: latestInstance!.tsEnd ? new Date(latestInstance!.tsEnd) : null,
  };
}

export function getSortedByActivityUnits(instances: GreenerUnitInstance[]) {
  const now = new Date();

  const isInstanceActive = (instance) => {
    const start = new Date(instance.tsStart);
    const end = instance.tsEnd ? new Date(instance.tsEnd) : null;
    return start <= now && (!end || now <= end);
  };

  const sortedInstances = instances.sort((a, b) => {
    const aIsActive = isInstanceActive(a);
    const bIsActive = isInstanceActive(b);

    if (aIsActive !== bIsActive) {
      return aIsActive ? -1 : 1;
    }

    return new Date(a.tsStart).getTime() - new Date(b.tsStart).getTime();
  });

  const sortedUnits = sortedInstances.map((instance) => instance.greenerUnit);
  return sortedUnits;
}

const DEVICE_TYPE_LABELS = {
  [DEVICE_TYPE_IDS.BATTERY]: 'components:unitSelector.battery',
  [DEVICE_TYPE_IDS.ENERGY_METER]: 'components:unitSelector.energyMeter',
};

export const getUnitTypeLabel = (t, deviceTypeId) => {
  return t(DEVICE_TYPE_LABELS[deviceTypeId]);
};
